import PropTypes from 'prop-types'
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Divider,
  Link,
} from '@chakra-ui/react'
import {useTranslation, Trans} from 'react-i18next'
import useRedemptionPartnerLogo from '../../hooks/useRedemptionPartnerLogo'

const RedeemPointsStatusModal = ({
  isOpen,
  onClose,
  redemptionStatus,
  partnerKey,
  convertAmount,
  partnerPoints,
  currentBalance,
}) => {
  const {t} = useTranslation('points')

  const PartnerLogo = useRedemptionPartnerLogo({
    redemptionPartnerKey: partnerKey,
  })

  const partnerMap = {
    VABF: {
      marketplaceUrl:
        'https://www.velocityfrequentflyer.com/business-flyer/partners-and-offers/online-shopping',
      travelUrl: 'https://business.velocityfrequentflyer.com/auth/login',
    },
    QBR: {
      marketplaceUrl:
        'https://www.qantas.com/au/en/business-rewards/use-points.html',
    },
  }

  const {marketplaceUrl, travelUrl} = partnerMap[partnerKey] || {
    marketplaceUrl: '#',
    travelUrl: '#',
  }

  if (redemptionStatus === 'failed') {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" preserveScrollBarGap>
        <ModalOverlay />
        <ModalContent
          m={{base: 0, md: 'auto'}}
          borderRadius={{base: 0, md: 'md'}}
          maxW={{base: '100vw', md: '2xl'}}
          h={{base: '100vh', md: 'auto'}}
        >
          <ModalHeader fontWeight="normal">
            {t('points:redeemModal.redeemFailed.title')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack>
              <Divider />
              {/* Content */}
              <VStack align="stretch" py="6" width="100%">
                <Box mb="4" display="flex" flexDirection="column" gap="4">
                  <Text>
                    {t('points:redeemModal.redeemFailed.description')}
                  </Text>
                  <Text>
                    <Trans
                      i18nKey="points:redeemModal.redeemFailed.contactSupport"
                      components={{
                        CustomerSupport: (
                          <Text as="span" textDecoration="underline" />
                        ),
                      }}
                    />
                  </Text>
                </Box>

                {/* Action Buttons */}
                <Flex gap="24px">
                  <Button
                    border="1px solid"
                    variant="secondary"
                    borderRadius="full"
                    onClick={onClose}
                  >
                    {t('points:redeemModal.buttons.done')}
                  </Button>
                </Flex>
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" preserveScrollBarGap>
      <ModalOverlay />
      <ModalContent
        m={{base: 0, md: 'auto'}}
        borderRadius={{base: 0, md: 'md'}}
        maxW={{base: '100vw', md: '2xl'}}
        h={{base: '100vh', md: 'auto'}}
      >
        <ModalHeader fontWeight="normal">
          {t('points:redeemModal.redeemProcessing.title')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pt="0">
          <VStack>
            <Divider borderColor="mld.neutral.900" />
            {/* Content */}
            <VStack align="stretch" width="100%">
              <Text textStyle="body-small">
                {t(
                  `points:redeemModal.redeemProcessing.partners.${partnerKey}.description`,
                )}
              </Text>
              <Flex
                p="md"
                direction={{base: 'column', sm: 'row'}}
                justify="space-between"
                align="center"
                bgColor="mld.neutral.50"
                borderRadius="lg"
                flexWrap="wrap"
              >
                <PartnerLogo maxWidth="160px" height="70px" />
                <Text fontSize="72px" lineHeight="0" letterSpacing="2">
                  <Trans
                    i18nKey={`points:redeemModal.redeemProcessing.partners.${partnerKey}.amount`}
                    values={{amount: partnerPoints.toLocaleString()}}
                    components={{
                      span: (
                        <Text
                          textStyle="body-small-medium"
                          textTransform="uppercase"
                          as="span"
                        />
                      ),
                    }}
                  />
                </Text>
              </Flex>
              <Text textStyle="body-detail" color="mld.neutral.700">
                <Trans
                  i18nKey={`points:redeemModal.redeemProcessing.partners.${partnerKey}.usageOptions`}
                  components={{
                    marketplaceLink: (
                      <Link
                        variant="primary"
                        href={marketplaceUrl}
                        isExternal
                        textStyle="body-detail"
                        color="mld.neutral.700"
                      />
                    ),
                    travelLink: (
                      <Link
                        variant="primary"
                        href={travelUrl}
                        isExternal
                        textStyle="body-detail"
                        color="mld.neutral.700"
                      />
                    ),
                  }}
                />
              </Text>

              {/* Balance Information */}
              <Flex
                direction="column"
                mt="sm"
                py="sm"
                borderY="1px solid"
                borderColor="mld.neutral.600"
                gap="sm"
              >
                <Text textStyle="headline5" textTransform="uppercase">
                  {t('points:redeemModal.redeemProcessing.pointBalance')}
                </Text>

                <HStack justifyContent="space-between">
                  <Text textStyle="body-copy">
                    {t('points:redeemModal.redeemProcessing.remainingBalance')}
                  </Text>
                  <Box
                    flexGrow={1}
                    borderBottom="1px dashed"
                    borderColor="mld.neutral.600"
                  />
                  <Text textStyle="body-copy-medium">
                    <Trans
                      i18nKey="points:redeemModal.redeemProcessing.mCoinAmount"
                      values={{
                        amount: currentBalance?.toLocaleString(),
                      }}
                      components={{
                        span: (
                          <Text
                            as="span"
                            textStyle="headline5"
                            textTransform="uppercase"
                            pl="xxs"
                          />
                        ),
                      }}
                    />
                  </Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text textStyle="body-copy">
                    {t('points:redeemModal.redeemProcessing.convertAmount')}
                  </Text>
                  <Box
                    flexGrow={1}
                    borderBottom="1px dashed"
                    borderColor="mld.neutral.600"
                  />
                  <Text textStyle="body-copy">
                    <Trans
                      i18nKey="points:redeemModal.redeemProcessing.mCoinAmount"
                      values={{
                        amount: (convertAmount || 43423)?.toLocaleString(),
                      }}
                      components={{
                        span: (
                          <Text
                            as="span"
                            textStyle="headline5"
                            textTransform="uppercase"
                            pl="xxs"
                          />
                        ),
                      }}
                    />
                  </Text>
                </HStack>
              </Flex>

              {/* Action Buttons */}
              <Flex py="sm">
                <Button
                  border="1px solid"
                  variant="secondary"
                  borderRadius="full"
                  onClick={onClose}
                >
                  {t('points:redeemModal.buttons.done')}
                </Button>
              </Flex>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

RedeemPointsStatusModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentBalance: PropTypes.number.isRequired,
  partnerKey: PropTypes.string.isRequired,
  partnerPoints: PropTypes.number.isRequired,
  convertAmount: PropTypes.number.isRequired,
  redemptionStatus: PropTypes.oneOf(['processing', 'failed']).isRequired,
}

export default RedeemPointsStatusModal
